import { LitElement, html, css } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { LIT_CSS_VARIABLES } from '@Root/lib/lit-styles.js';
import { SHARED_ELEMENTS, SHARED_CLASSES } from '../../styles';
import { icon } from '../../icons';
import {
  fireAdobeAnalyticsEvent,
  handleFileDownloadEvent,
  analyticsCtaDataObject,
  handleAdobeNavEvent,
  withBlockPlacement,
} from '../../../../helpers/adobe-analytics';
import {
  CONTACT_REP_HASH,
  openContactRepModal,
} from '../../../../helpers/contact-rep-modal';
class CtaButton extends LitElement {
  static get properties() {
    return {
      hidden: { type: Boolean },
      disabled: { type: Boolean },
      ctaData: { type: Object },
      ctaAnalyticsData: { type: Object },
    };
  }

  constructor() {
    super();
    this.ctaAnalyticsData = {};
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      css`
        a {
          display: flex;
          flex-direction: column;
          justify-content: center;
          border: 1px solid var(--block-text-color, #222);
          width: max-content;
          /* Design floor, so short labels still get a full-size button. */
          min-width: calc(200rem / var(--default-rem-px-count));
          /* This is sometimes not rendering as 42px for some reason */
          /* height: calc(42px / var(--default-rem-px-count)); */
          height: 44px;
          cursor: pointer;
        }

        a > .cta-button {
          width: 100%;
          display: flex;
          justify-content: space-between;
          white-space: nowrap;
          height: fit-content;
          align-items: center;
        }

        a > .cta-button > p {
          /* CTA type tokens; same rendered values (14/17.5/-0.24/500). */
          font-size: var(--fs-cta);
          font-weight: var(--fw-medium);
          letter-spacing: var(--ls-cta);
          line-height: var(--lh-cta);
          padding-left: calc(16rem / var(--default-rem-px-count));
          padding-right: calc(12rem / var(--default-rem-px-count));
          margin-bottom: calc(-2rem / var(--default-rem-px-count));
          text-wrap: balance;
          height: fit-content;
        }

        svg {
          padding-right: calc(16rem / var(--default-rem-px-count));
          width: calc(16rem / var(--default-rem-px-count));
          height: calc(16rem / var(--default-rem-px-count));
        }

        svg.smaller-svg {
          width: calc(12rem / var(--default-rem-px-count)) !important;
          height: calc(12rem / var(--default-rem-px-count)) !important;
        }

        /* The solid button is a fixed pair in the design system (Figma
           21248:17269) and does not retint with the block — inverting it
           makes a themed block render a light chip with tinted text. The
           outline button below carries the theme instead. Figma's solid
           button has no border either, so the one the shared rule draws has
           to match the fill. */
        .primary-colors,
        .primary-colors > span > p {
          background-color: #222222;
        }

        .primary-colors {
          border-color: #222222;
        }

        /* SHARED_ELEMENTS styles a:visited/:hover/:active at (0,1,1), which
           outranks a bare class, so the link states are spelled out here. The
           label is pinned separately and would survive on its own; the arrow
           inherits from the <a>, and without this it falls back to the
           block's text color on a visited or hovered CTA. */
        .primary-colors,
        .primary-colors:visited,
        .primary-colors:hover,
        .primary-colors:active,
        .primary-colors > span > p {
          color: #ffffff;
        }

        .secondary-colors {
          background-color: transparent;
          color: inherit;
        }

        /* Hover/active: both variants grow 0.5px on all sides via an outer
           0.5px ring flush against the edge — matches Figma's 1px→1.5px
           border spec, with the extra 0.5px extending outward. box-shadow
           keeps nothing below the button from shifting. */
        a.primary-colors:not(.disabled):hover {
          box-shadow: 0 0 0 0.5px #222;
        }

        a.secondary-colors:not(.disabled):hover,
        a.secondary-colors:not(.disabled):active {
          box-shadow: 0 0 0 0.5px var(--block-text-color, #222);
        }

        /* Active/pressed: solid → medium grey (ring retints to match new bg
           so the enlarged silhouette is preserved); outline → light grey fill */
        a.primary-colors:not(.disabled):active,
        a.primary-colors:not(.disabled):active > span > p {
          background-color: #979797;
          border-color: #979797;
        }

        a.primary-colors:not(.disabled):active {
          box-shadow: 0 0 0 0.5px #979797;
        }

        a.secondary-colors:not(.disabled):active {
          background-color: var(--block-pressed-tint);
        }

        a.disabled {
          cursor: default;
          opacity: 0.2;
        }

        a:focus-visible {
          outline: 2px solid #ff993d;
          outline-offset: 2px;
        }
      `,
    ];
  }

  render() {
    const actsAsButton = !this.urlString;
    return !!this.parsedCtaData
      ? html`
          <a
            href=${ifDefined(this.urlString)}
            target=${ifDefined(this.targetValue)}
            tabindex=${ifDefined(
              actsAsButton && !this.disabled ? '0' : undefined
            )}
            role=${ifDefined(actsAsButton ? 'button' : undefined)}
            aria-disabled=${ifDefined(
              actsAsButton && this.disabled ? 'true' : undefined
            )}
            class="${classMap(this.linkClasses)}"
            style="${styleMap(this.buttonWidth)}"
            @click="${this._handleClick}"
            @keydown="${this._handleKeydown}"
          >
            <span class="cta-button">
              <p>
                ${this.textString}
              </p>
              ${icon(this.iconName, this.isDownload ? 'smaller-svg' : '')}
            </span>
          </a>
        `
      : html``;
  }

  _handleClick(event) {
    if (this.disabled) {
      event.preventDefault();
      return;
    }
    if (this.urlString === CONTACT_REP_HASH) {
      openContactRepModal();
    }
    this.sendAnalyticsEvent(event);
  }

  _handleKeydown(event) {
    if (this.urlString || this.disabled) return;
    if (
      event.key === 'Enter' ||
      event.key === ' ' ||
      event.key === 'Spacebar'
    ) {
      event.preventDefault();
      event.currentTarget.click();
    }
  }

  get isPrimary() {
    return (
      this.getAttribute('cta-type') === 'primary' ||
      this.parsedCtaData?.isPrimary
    );
  }

  get buttonWidth() {
    if (this.parsedCtaData?.fullWidth) {
      return { width: '100%' };
    } else if (this.parsedCtaData?.standardWidth) {
      // min-width too: the design floor below would otherwise hold the button
      // at 200px and overflow the column the 40vw cap exists to stay inside.
      return { width: 'min(220px, 40vw)', minWidth: 'min(220px, 40vw)' };
    } else {
      return { width: 'max-content' };
    }
  }

  get iconName() {
    return this.isDownload ? 'download' : 'arrow';
  }

  get adobeAnalyticsDataString() {
    return this.parsedCtaData?.analyticsName || '';
  }

  get parsedCtaAnalyticsData() {
    if (Object.keys(this.ctaAnalyticsData).length) return this.ctaAnalyticsData;

    this.ctaAnalyticsData = this.getAttribute('cta-analytics-data')
      ? JSON.parse(this.getAttribute('cta-analytics-data'))
      : {};

    this.ctaAnalyticsData.originalObject = this.parsedCtaData;
    return this.ctaAnalyticsData;
  }

  // The raw value the block handed in, before parsedCtaAnalyticsData stamps
  // originalObject onto it and makes "empty" indistinguishable from "given".
  get suppliedCtaAnalyticsData() {
    if (typeof this.ctaAnalyticsData === 'string') return this.ctaAnalyticsData;
    if (Object.keys(this.ctaAnalyticsData || {}).length) {
      return this.ctaAnalyticsData;
    }

    return this.getAttribute('cta-analytics-data') || null;
  }

  sendAnalyticsEvent(event) {
    if (this.isDownload) {
      // The block's own object carries the placement; only rebuild from
      // cta-data when it did not supply one.
      handleFileDownloadEvent(
        withBlockPlacement(
          this.suppliedCtaAnalyticsData ||
            analyticsCtaDataObject(this.parsedCtaData),
          this
        )
      );
    } else if (!!this.adobeAnalyticsDataString) {
      const eventData = {
        type: 'click',
        detail: {
          eVar8: this.adobeAnalyticsDataString,
        },
      };
      fireAdobeAnalyticsEvent(eventData);
    } else if (this.parsedCtaAnalyticsData) {
      const ctaAnalyticsObj = withBlockPlacement(
        this.parsedCtaAnalyticsData,
        this
      );

      ctaAnalyticsObj.originalObject = this.parsedCtaData;

      if (ctaAnalyticsObj.isDownload) {
        handleFileDownloadEvent(ctaAnalyticsObj);
      } else {
        ctaAnalyticsObj.event = event;
        handleAdobeNavEvent(ctaAnalyticsObj);
      }
    }
  }

  get textString() {
    return this.parsedCtaData?.text || '';
  }

  get urlString() {
    return this.parsedCtaData?.url || undefined;
  }

  get targetValue() {
    if (this.isDownload) {
      return '_blank';
    }

    return this.parsedCtaData?.target ?? '_self';
  }

  get isDownload() {
    return (
      this.parsedCtaData?.isDownload ||
      this.getAttribute('cta-type') === 'download' ||
      this.isTechSpecsUrl
    );
  }

  get isTechSpecsUrl() {
    if (!this.urlString) return false;

    try {
      const url = this.urlString.startsWith('/')
        ? this.urlString
        : new URL(this.urlString).pathname;

      return /^\/tech-specs\/\d+\/?$/.test(url);
    } catch {
      return false;
    }
  }

  get linkClasses() {
    return {
      'primary-colors': this.isPrimary,
      'secondary-colors': !this.isPrimary,
      hidden: this.hidden,
      disabled: this.disabled,
      'print-none': !this.reserveSpaceWhenHidden,
      'print-hidden': this.reserveSpaceWhenHidden,
    };
  }

  get parsedCtaData() {
    if (this.ctaData) {
      return this.ctaData;
    }

    const ctaDataAttribute = this.getAttribute('cta-data');

    if (ctaDataAttribute) {
      this.ctaData = JSON.parse(ctaDataAttribute);
      return this.ctaData;
    }

    return this.ctaData || null;
  }

  get reserveSpaceWhenHidden() {
    return this.hasAttribute('reserve-space-when-hidden');
  }
}
customElements.define('cta-button', CtaButton);
